import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { IconBack, InformationalScreen, LoadingModal, Stack, } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ApiStatus, ErrorCode } from 'Constants';
import axios from 'axios';
import { useApiError } from 'Hooks';
import { Button, Footer, MainContainer, OptInOut, RichText } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { SHOW_MODAL } from 'Reducers/modal/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { getDocumentPathFromLanguage } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { logout } from 'Utilities/session';
import { store } from 'Utilities/store';
import { handleConsent } from 'Services/consent';
const mapStateToProps = ({ env: { osType, osVersion, app, appVersion, phoneLanguage }, auth, nav: { query }, config: { documentUrls, documentPaths }, }) => {
    return {
        osType,
        osVersion,
        app,
        appVersion,
        auth,
        isAccountProfile: !!query.accountProfile,
        cmsDomain: documentUrls.cms,
        rweCmsPaths: documentPaths.cms?.rwelibre,
        phoneLanguage,
    };
};
const connector = connect(mapStateToProps);
const RWEConsent = ({ osType, osVersion, isAccountProfile, app, appVersion, auth, cmsDomain, rweCmsPaths, phoneLanguage, }) => {
    const [loading, setLoading] = useState(false);
    const [isConsentOutdated, setIsConsentOutdated] = useState(false);
    const [rweBodyText, setRweBodyText] = useState(i18n.t('Research.content.researchRichText.bodyText'));
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const fetchRweConsentText = useCallback(async () => {
        try {
            const rweCmsUrl = getDocumentPathFromLanguage(rweCmsPaths, phoneLanguage);
            const rweCmsFullDomain = `${cmsDomain}${rweCmsUrl}`;
            const response = await axios.get(rweCmsFullDomain);
            const data = response.data;
            if (!data?.Research?.content?.researchRichText?.bodyText) {
                setLoading(false);
                return;
            }
            setRweBodyText(data.Research.content.researchRichText.bodyText);
            store.dispatch({
                type: SHOW_MODAL,
                modalProps: {
                    details: i18n.t('Consents.modals.reviewConsent.body'),
                    testID: 'ReviewConsent',
                },
            });
            setIsConsentOutdated(true);
            setLoading(false);
        }
        catch {
            setLoading(false);
        }
    }, [phoneLanguage, cmsDomain, rweCmsPaths]);
    // Disable consents runtime update for release/3.23.1
    // useEffect(() => {
    //   if (isAccountProfile && auth.include.patient.consents?.realWorldEvidence) {
    //     const { at, latestVersion } = auth.include.patient.consents.realWorldEvidence;
    //     const isOutdated = at < latestVersion;
    //     if (isOutdated) {
    //       fetchRweConsentText();
    //     }
    //   }
    //   setLoading(false);
    // }, [auth, fetchRweConsentText, isAccountProfile]);
    const handleClickLink = (href) => {
        mediator.publish('router:navigate', href);
    };
    const handleApiRequestError = useCallback((error) => {
        switch (error.status) {
            case ApiStatus.UNAUTHORIZED:
                logout();
                break;
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [showApiErrorModal, showNetworkErrorModal]);
    const handleConsentWrapper = useCallback(async (action) => {
        return new Promise((resolve, reject) => {
            handleConsent(auth.account_id, action, osType, osVersion, app, appVersion, 'realWorldEvidence')
                .then((resp) => {
                const updatedAuthSession = {
                    ...auth,
                    include: {
                        patient: {
                            ...auth.include.patient,
                            consents: { ...auth.include.patient.consents, realWorldEvidence: { ...resp } },
                        },
                    },
                };
                store.dispatch({ type: AUTH_SESSION, authSession: updatedAuthSession });
                notifyApp('adc-webview:consents', {
                    id: 'realWorldEvidence',
                    action,
                    location: isAccountProfile ? 'profile' : 'registration',
                });
                resolve(updatedAuthSession);
            })
                .catch((error) => {
                handleApiRequestError(error);
                reject(error);
            });
        });
    }, [app, appVersion, auth, handleApiRequestError, isAccountProfile, osType, osVersion]);
    const handleSubmitRWEAction = useCallback(async (action) => {
        try {
            setLoading(true);
            const updatedAuthSession = await handleConsentWrapper(action);
            notifyApp('adc-webview:signin', updatedAuthSession);
            setIsConsentOutdated(false);
            if (isAccountProfile) {
                // Ends the loading state if the user is on the account profile page
                setLoading(false);
                return;
            }
            // This timeout is added to give the app time to redirect the user back.
            setTimeout(() => {
                setLoading(false);
            }, APP_RETURN_TIMEOUT);
        }
        catch (err) {
            setLoading(false);
        }
    }, [handleConsentWrapper, isAccountProfile]);
    const onClosePressed = useCallback(() => {
        mediator.publish('router:back');
    }, []);
    return (React.createElement(MainContainer, null,
        loading && React.createElement(LoadingModal, { "aria-label": "Loading" }),
        React.createElement(InformationalScreen, { topNavigationProps: {
                animatedTitle: true,
                title: i18n.t('Research.title'),
                buttonStart: isAccountProfile && !isConsentOutdated
                    ? {
                        element: React.createElement(IconBack, { testID: 'Research.close-Pressable' }),
                        label: i18n.t('Research.close'),
                        onPress: onClosePressed,
                    }
                    : undefined,
            }, testID: "Research" },
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(RichText, { testID: "Research.content.researchRichText.bodyText", onPressLink: handleClickLink, text: rweBodyText })),
                React.createElement(InformationalScreen.Footer, null, isConsentOutdated || !isAccountProfile ? (React.createElement(Footer, null,
                    React.createElement(Stack, { alignSelf: "flex-end", width: "100%", gap: "$4" },
                        React.createElement(Button, { onPress: () => handleSubmitRWEAction('accept'), testID: "RWE.optIn", label: i18n.t('Global.microcopy.common.optIn'), "aria-label": i18n.t('Global.microcopy.common.optIn') }),
                        React.createElement(Button, { variant: "tertiary", onPress: () => handleSubmitRWEAction('decline'), label: i18n.t('Global.microcopy.common.decline'), "aria-label": i18n.t('Global.microcopy.common.decline'), testID: "Global.microcopy.common.decline" })))) : (React.createElement(OptInOut, { optionStrings: {
                        titleTag: 'Global.microcopy.common.status',
                        positiveTag: 'Global.microcopy.common.optedIn',
                        negativeTag: 'Global.microcopy.common.declined',
                        positiveOption: 'Global.microcopy.common.optIn',
                        negativeOption: 'Global.microcopy.common.decline',
                    }, initialState: !!auth.include.patient?.consents?.realWorldEvidence?.accepted
                        ? 'accept'
                        : 'decline', handleConsent: handleConsentWrapper })))))));
};
export default connector(RWEConsent);
